import { Stack, Group, Grid } from '@mantine/core'
import React, { useImperativeHandle, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isNotEmpty, useForm } from '@mantine/form'
import BlackTextInput from 'app/views/components/inputs/BlackTextInput'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { getFacilityLendersPositionsWithLoading } from 'app/state/ducks/lenders-positions/selectors'
import { changeAllLendersForFacility, loadLendersPositionsByFacility } from 'app/state/ducks/lenders-positions/thunks'
import { StableKey } from 'app/models/common-types'
import { getFacilityAmendment } from 'app/state/ducks/facility-amendments/selectors'
import { FacilityParams } from 'app/models/facility-params'
import { saveFacilityAmendment } from 'app/state/ducks/facility-amendments/thunks'
import {
  ErrorNotification,
  SuccessNotification,
} from '../notifications/notification'
import { formatDateToUTC, formatNumberToCurrency, stringToDate } from 'app/utils/util-functions'
import StepHeader from '../modals/step-header'
import IonIcon from '@reacticons/ionicons'
import { getBusinessDate } from 'app/state/ducks/business-date/selectors'
import { EntityRole } from 'app/models/entity-profile-params'
import { getEntityProfiles } from 'app/state/ducks/entity-profile/selectors'
import UploadCSV from '../upload-csv/upload-csv'
import { transformLendersCSVToSubmission } from './lenders-transformCSV'
import LenderPositionField from './lender-position-field'
import WarningText from '../warning-text/warning-text'

type Props = {
  facility: any
  setFacility: any
  amendment?: string | null
  onClose?: () => void
}

interface ILendersArray {
  label: string
  value: string
  disabled: boolean
}

interface LenderPosition {
  lenders: StableKey
  amount: number
  id?: string | null
  newField?: boolean | null
}

export interface ChildManageLendersRef {
  handleClick: () => void
}

const defaultValue = {
  id: null,
  percentage: '',
  amount: 0,
  lenderName: '',
  lenders: { id: '', admin: '' },
  creditContactId: null,
  adminContactId: null,
  wireInstructions: [],
}

const ManageLenders: React.ForwardRefRenderFunction<
  ChildManageLendersRef,
  Props
> = ({ facility, amendment, setFacility, onClose }, ref) => {
  const dispatch = useDispatch()
  const businessDate = useSelector(getBusinessDate)
  const entities = useSelector(getEntities)
  const entityProfiles = useSelector(getEntityProfiles)
  const facilityAmendment: FacilityParams = useSelector(getFacilityAmendment)
  const { data: lendersPositionsData, isLoading: isLoadingLenders } = useSelector(
    getFacilityLendersPositionsWithLoading
  )
  let lendersPositions = lendersPositionsData
  let disableLenderPositions = false

  if (
    facility &&
    facility.amendmentLenders &&
    facility.amendmentLenders.length > 0
  ) {
    lendersPositions = facility.amendmentLenders
  } else {
    lendersPositions.map(lp => {
      if (lp.proRatas) {
        const prs = lp.proRatas
        prs.sort(function (a, b) {
          const arr1 = a.effectiveDate.split('-')
          const arr2 = b.effectiveDate.split('-')
          const date1 = new Date(
            parseInt(arr1[0]),
            parseInt(arr1[1]),
            parseInt(arr1[2])
          )
          const date2 = new Date(
            parseInt(arr2[0]),
            parseInt(arr2[1]),
            parseInt(arr2[2])
          )
          return date1.getTime() - date2.getTime()
        })
        const latestProRata = prs[prs.length - 1]
        if (stringToDate(facility?.startDate) < stringToDate(latestProRata?.effectiveDate))
          disableLenderPositions = true
        lp.amount = latestProRata.amount
      }
    })
  }

  const [lendersArray, setLendersArray] = useState<ILendersArray[]>([])

  const handleClick = async () => {
    const transformedPositions = form.getTransformedValues().lendersPosition
    if (disableLenderPositions) return
    if (form.validate().hasErrors === true) {
      throw Error
    } else {
      let response: any
      if (amendment) {
        const amendmentLenders = transformedPositions.map(
          (lp: { lender: StableKey; amount: string; id?: string }) => {
            return { lender: lp.lender, amount: lp.amount, id: lp.id }
          }
        )
        response = await dispatch(
          saveFacilityAmendment(
            {
              ...facility,
              amendmentDate: facility.amendmentDate ?? formatDateToUTC(businessDate),
              amendmentLenders: amendmentLenders,
              amendmentAmortization: facility.amendmentAmortization ?? null,
              amendmentFees: facility.amendmentFees ?? [],
            },
            facilityAmendment
          )
        )
        if (response?.success === false) {
          ErrorNotification({
            title: ' Facility Amendment Failed',
            message:
              response?.payload || 'Check Lender Allocations and try again',
          })
          return
        }
        setFacility({
          ...facility,
          amendmentLenders: amendmentLenders,
        })
        SuccessNotification({
          title: 'Successful Facility Amendment',
          message:
            'You can amend Lender Allocations, Interest Rate Options, Fees, and Amortization Schedules',
        })
        return
      }
      try {
        await dispatch(
          changeAllLendersForFacility(transformedPositions, {
            id: facility.id,
            admin: facility.accountManagementAdmin,
          })
        )
        SuccessNotification({
          title: 'Successful Facility Creation',
          message:
            'You can add Lender Allocations, Interest Rate Options, Fees and Amortization Schedules',
        })
      } catch (error) {
        console.error(error)
        ErrorNotification({
          title: ' Facility Creation Failed',
          message: 'Check Lender Allocations and try again',
        })
      }
    }
  }

  // Expose the handleClick method to the parent component
  useImperativeHandle(ref, () => ({
    handleClick,
  }))

  React.useEffect(() => {
    const lendersPosition =
      lendersPositions?.length > 0
        ? lendersPositions.map(
          (lp: { lender: StableKey; amount: string; id?: string }) => ({
            lenders: lp.lender,
            amount: Number(lp.amount),
            id: lp.id,
          })
        )
        : [{ ...defaultValue }]
    const subtotal =
      lendersPositions.length > 0
        ? lendersPositions.reduce(
          (accumulator: number, lp: { amount: string }) =>
            accumulator + Number(lp.amount),
          0
        )
        : 0
    form.setFieldValue('lendersPosition', lendersPosition)
    form.setFieldValue('subtotal', subtotal)
  }, [lendersPositions])


  React.useEffect(() => {
    const lenderallocations =
      entities && entities.length > 0
        ? entities
          ?.filter(entity =>
            entity.entityProfile.some(profile => entityProfiles.find(option => option.id === profile.id)?.roles.includes(EntityRole.LenderRole)))
          .map(entity => ({
            value: entity.id || '',
            label: entity.entityName,
            disabled: false,
          }))
        : []
    setLendersArray(lenderallocations)
  }, [facility, lendersPositions])

  const transformLendersPositionValues = (values: any) => ({
    ...values,
    lendersPosition: values.lendersPosition.map(
      (a: { lenders: { id: string }; amount: number }) => {
        return {
          ...a,
          lender: {
            id: a.lenders.id,
            admin:
              entities.find(entity => entity.id === a.lenders.id)?.maker ?? '',
          },
          amount: a.amount.toString(),
        }
      }
    ),
  })

  const form = useForm({
    initialValues: {
      lendersPosition:
        lendersPositions?.length > 0
          ? lendersPositions.map(
            (lp: { lender: StableKey; amount: string; id?: string }) => ({
              lenders: lp.lender,
              amount: Number(lp.amount),
              id: lp.id,
            })
          )
          : [{ ...defaultValue }],
      subtotal:
        lendersPositions.length > 0
          ? lendersPositions.reduce(
            (accumulator: number, lp: { amount: string }) =>
              accumulator + Number(lp.amount),
            0
          )
          : 0,
    },

    transformValues: transformLendersPositionValues,

    validate: {
      lendersPosition: {
        amount: value => (value >= 0 ? null : 'Invalid amount'),
        lenders: { id: isNotEmpty(' Choose a lender') },
      },
      subtotal: value => {
        const diff = value - facility?.amount
        return diff < .001 ? null : 'Cannot be greater than Facility'
      },
    },
  })

  const handleCSVData = async (parsedData: Record<string, string>[]) => {
    const lenderAllocationsFacility = transformLendersCSVToSubmission(parsedData, entities, transformLendersPositionValues)
    const dispatchPromises = Object.keys(lenderAllocationsFacility).map(async (facilityId) => {
      const transformedPositions = lenderAllocationsFacility[facilityId].flatMap(
        (allocation: any) => allocation.lendersPosition
      );
      return dispatch(
        changeAllLendersForFacility(transformedPositions, {
          id: facilityId,
          admin: facility.accountManagementAdmin,
        }, false)
      );
    });

    const results = await Promise.all(dispatchPromises);

    console.log("Dispatch results:", results);
    dispatch(loadLendersPositionsByFacility(facility.id))
    SuccessNotification({
      title: 'Successfully Saved Bulk Lender Positions',
      message: 'You created many lender positions',
    })
  }

  form.values.subtotal = form.values?.lendersPosition
    .map(lp => (lp.amount ? lp.amount : 0))
    .reduce(
      (accumulator: number, currentValue: number) => accumulator + currentValue,
      0
    )
  const facilityAmount = Number(facility?.amount)
  const selectedOptions = form.values.lendersPosition?.map(
    (item: { lenders: { id: string } }) => item.lenders.id
  )
  const fields = form.values.lendersPosition?.map(
    (
      item: LenderPosition,
      index: number
    ) => (
      <LenderPositionField
        key={item.id || `new-lender-${index}`}
        form={form}
        index={index}
        lendersArray={lendersArray}
        selectedOptions={selectedOptions}
        disableLenderPositions={disableLenderPositions}
        amendment={amendment}
        facility={facility}
      />
    ))

  return !isLoadingLenders ?
    (
      <>
        {onClose && (
          <StepHeader
            title={`${facility?.name ? 'Edit' : 'Create'} Lender Allocations`}
            onClose={onClose}
          />
        )}
        <div style={{ position: 'relative' }}>
          <Group
            noWrap
            w="100%"
            position='apart'
            style={{ position: 'absolute', top: '-32px', right: '0', zIndex: 1 }}
          >
            <div></div>
            <UploadCSV onParse={handleCSVData} id="csvInputLenderAllocations" />
          </Group>
        </div>
        <Stack mih={600} align="flex-start" mt={30}>
          <Stack w="100%" mt={20}>
            {/* Column Headers */}
            <Grid
              align="center"
              gutter={0}
              mb={0}
              style={{ padding: '0 20px' }}
              sx={{
                '& .mantine-Grid-col': {
                  padding: '0 !important'
                }
              }}
            >
              <Grid.Col span="auto" style={{ maxWidth: '50px' }}>
                {/* Empty space for row numbers */}
              </Grid.Col>
              <Grid.Col span={8} style={{ marginRight: '10px' }}>
                <div className="lender-allocation-column-header">
                  Lender
                </div>
              </Grid.Col>
              <Grid.Col span={3}>
                <div className="lender-allocation-column-header" style={{ textAlign: 'right' }}>
                  Amount
                </div>
              </Grid.Col>
              <Grid.Col span="auto" style={{ maxWidth: '40px' }}>
                {/* Empty space for close button */}
              </Grid.Col>
            </Grid>
            {fields}
            {!disableLenderPositions && (
              <Grid
                align="center"
                gutter={0}
                style={{
                  padding: '10px 20px 10px 20px'
                }}
                sx={{
                  '& .mantine-Grid-col': {
                    padding: '0 !important'
                  }
                }}
              >
                <Grid.Col span="auto" style={{ maxWidth: '50px' }}>
                  {/* Empty space for row numbers */}
                </Grid.Col>
                <Grid.Col span={11}>
                  <button
                    type="button"
                    className="lender-allocation-add-button"
                    style={{
                      width: 'calc(100% + 10px)',
                      marginLeft: '-3px'
                    }}
                    onClick={() =>
                      form.insertListItem('lendersPosition', {
                        ...defaultValue,
                        newField: true,
                      })
                    }
                  >
                    <IonIcon name="add-circle-outline" style={{ width: '18px', height: '18px' }} />
                    Add lender
                  </button>
                </Grid.Col>
                <Grid.Col span="auto" style={{ maxWidth: '40px' }}>
                  {/* Empty space for close button */}
                </Grid.Col>
              </Grid>
            )}

            <Grid
              align="center"
              gutter={0}
              mt={20}
              style={{
                borderTop: '2px solid #E5E7EB',
                padding: '20px 20px 0 20px'
              }}
              sx={{
                '& .mantine-Grid-col': {
                  padding: '0 !important'
                }
              }}
            >
              <Grid.Col span="auto" style={{ maxWidth: '40px' }}>
                {/* Empty space for row numbers */}
              </Grid.Col>
              <Grid.Col span={8} style={{ marginRight: '10px' }}>
                <div className="lender-allocation-subtotal-label">
                  Subtotal
                </div>
              </Grid.Col>
              <Grid.Col span={3}>
                <BlackTextInput
                  placeholder="Allocation Total"
                  disabled
                  value={formatNumberToCurrency(form.values.subtotal || 0, facility?.baseCurrency ?? 'USD')}
                  classNames={{
                    input: "lender-allocation-subtotal-input"
                  }}
                  styles={{
                    input: {
                      textAlign: 'right'
                    }
                  }}
                />
              </Grid.Col>
              <Grid.Col span="auto" style={{ maxWidth: '40px' }}>
                {/* Empty space for close button */}
              </Grid.Col>
            </Grid>
            {disableLenderPositions ? (
              <Grid
                align="center"
                gutter={0}
                mb={0}
                style={{ padding: '0 20px', marginTop: '20px' }}
                sx={{
                  '& .mantine-Grid-col': {
                    padding: '0 !important'
                  }
                }}
              >
                <Grid.Col span="auto" style={{ maxWidth: '40px' }}>
                  {/* Empty space for row numbers */}
                </Grid.Col>
                <Grid.Col span={11}>
                  <div
                    className="lender-allocation-centered-warning"
                    style={{
                      width: 'calc(100% + 10px)',
                      marginLeft: '-3px'
                    }}
                  >
                    <WarningText
                      type="error"
                      text="Lender Positions cannot be changed due to outstanding transactions."
                    />
                  </div>
                </Grid.Col>
                <Grid.Col span="auto" style={{ maxWidth: '40px' }}>
                  {/* Empty space for close button */}
                </Grid.Col>
              </Grid>
            ) : null}
            {form.values.subtotal - facilityAmount > .001 ? (
              <Grid
                align="center"
                gutter={0}
                mb={0}
                style={{ padding: '0 20px', marginTop: '20px' }}
                sx={{
                  '& .mantine-Grid-col': {
                    padding: '0 !important'
                  }
                }}
              >
                <Grid.Col span="auto" style={{ maxWidth: '40px' }}>
                  {/* Empty space for row numbers */}
                </Grid.Col>
                <Grid.Col span={11}>
                  <div
                    className="lender-allocation-centered-warning"
                    style={{
                      width: 'calc(100% + 10px)',
                      marginLeft: '-3px'
                    }}
                  >
                    <WarningText
                      type="error"
                      text="Total Lender Allocations should not exceed Facility Amount."
                    />
                  </div>
                </Grid.Col>
                <Grid.Col span="auto" style={{ maxWidth: '40px' }}>
                  {/* Empty space for close button */}
                </Grid.Col>
              </Grid>
            ) : null}
          </Stack>
        </Stack>
      </>
    ) : <Stack></Stack>
}
export default React.forwardRef(ManageLenders)
